import { useQuery } from '@tanstack/react-query'
import useResource from '../../../../io/useResource'

type UseInitialPayloadParams = {
  seedQueryKey: string
  existingResourceKey: string | undefined
  seedContent: (() => Promise<string | undefined>) | undefined
}

/**
 * Resolves an asset's initial payload before the editor mounts: the asset.resource content when present, otherwise the
 * config-supplied fallback (e.g. legacy form-service content). Returns undefined while still loading.
 *
 * Resolving up front lets the editor seed its edit state with a plain useState initializer instead of an in-place
 * effect. There is no "seed once / don't clobber edits" bookkeeping and nothing for StrictMode's mount/cleanup/mount to
 * cancel — react-query owns the fetch and dedupes it.
 */
const useInitialPayload = ({ seedQueryKey, existingResourceKey, seedContent }: UseInitialPayloadParams) => {
  const { content: resourceContent } = useResource(existingResourceKey)

  const seedQuery = useQuery({
    queryKey: ['assetPayloadSeed', seedQueryKey],
    queryFn: () => seedContent!().then((value) => value ?? ''),
    enabled: !existingResourceKey && !!seedContent,
  })

  if (existingResourceKey) return resourceContent
  if (seedContent) return seedQuery.data
  return ''
}

export default useInitialPayload
